import api from './api'

/**
 * SUB-3.13 · FR59, FR62, NFR5. The platform's structural history and the
 * relationship listing that goes with it.
 *
 * Both surfaces are superadmin-only and both are gated in the DATABASE, not
 * here: `list_tenant_hierarchy()` and `list_tenant_hierarchy_events()` raise
 * 42501 for anyone else. The backend forwards the caller's own access token so
 * those gates can see who is asking, which is why a 403 from either of these is
 * a real refusal and not a misconfiguration.
 */

/**
 * One row of `public.list_tenant_hierarchy()`.
 *
 * ⚠️ `used` IS NOT `rows for this parent`. It comes from `sub_account_usage()`,
 * which excludes archived children (FR67), while this listing shows them. A
 * parent with one archived client account correctly reads "0 of 5" beside one
 * row. Never recompute it from the rows: the figure below is the one the cap is
 * actually enforced against when a client account is created.
 */
export interface HierarchyPairing {
  parent_tenant_id: string
  parent_company_name: string
  parent_sub_accounts_enabled: boolean
  /** null means unlimited. 0 is a real value and means frozen (FR68). */
  allowance: number | null
  used: number
  unlimited: boolean
  at_limit: boolean
  /**
   * null for a parent that has sub-accounts enabled and no client accounts yet.
   * That row exists on purpose: a super admin who has just granted an allowance
   * of 5 should see "0 of 5", not an empty page.
   */
  child_tenant_id: string | null
  child_company_name: string | null
  child_is_archived: boolean | null
  child_created_at: string | null
}

/** The vocabulary `tenant_hierarchy_events.event_type` is constrained to. */
export type HierarchyEventType =
  | 'create'
  | 'create_standalone'
  | 'attach'
  | 'detach'
  | 'transfer'
  | 'enable'
  | 'disable'
  | 'allowance_change'
  | 'archive'
  | 'unarchive'
  | 'delete'

/** The governed columns a record snapshots. Never the whole tenant row. */
export interface HierarchySnapshot {
  company_name?: string | null
  parent_tenant_id?: string | null
  parent_company_name?: string | null
  sub_accounts_enabled?: boolean | null
  sub_accounts_allowance?: number | null
  is_archived?: boolean | null
}

export interface HierarchyEvent {
  id: number
  /**
   * One statement can change more than one axis: disabling sub-accounts also
   * clears the allowance, and both are recorded. Rows sharing this value
   * happened in the same statement.
   */
  change_group_id: string
  tenant_id: string
  /**
   * Snapshotted at write time, which is the only reason a delete record is
   * readable at all: after the delete the id resolves to nothing.
   */
  company_name: string
  parent_before: string | null
  parent_after: string | null
  event_type: HierarchyEventType
  actor_user_id: string | null
  actor_email: string | null
  /**
   * 'system' means `auth.uid()` was null when the change happened: service_role,
   * cron, or someone running SQL directly. The record is still written, and
   * "no identifiable actor" is a value rather than a blank to interpret.
   */
  actor_source: 'user' | 'system'
  occurred_at: string
  before: HierarchySnapshot | null
  after: HierarchySnapshot | null
}

export interface HierarchyEventQuery {
  /**
   * Matches the account AND both sides of a transfer, so asking about an agency
   * returns the moves of its client accounts as well as its own changes.
   */
  tenantId?: string
  eventTypes?: HierarchyEventType[]
  /** Clamped to 500 by the database, not by this client. */
  limit?: number
  /** Keyset pagination: ask for records older than this id. */
  beforeId?: number
}

/** Labels for the UI. Kept beside the type so a new event type is a type error. */
export const HIERARCHY_EVENT_LABELS: Record<HierarchyEventType, string> = {
  create: 'Client account created',
  create_standalone: 'Account created',
  attach: 'Attached to a provider',
  detach: 'Released to standalone',
  transfer: 'Moved to another provider',
  enable: 'Sub-accounts enabled',
  disable: 'Sub-accounts disabled',
  allowance_change: 'Allowance changed',
  archive: 'Archived',
  unarchive: 'Restored',
  delete: 'Deleted',
}

class HierarchyService {
  /** FR62. Every parent and sub-account pairing on the platform. */
  async listRelationships(): Promise<HierarchyPairing[]> {
    const response = await api.get('/tenants/hierarchy/relationships')
    return response.data
  }

  /** FR59, NFR5. The structural history, newest first. */
  async listEvents(query: HierarchyEventQuery = {}): Promise<HierarchyEvent[]> {
    const response = await api.get('/tenants/hierarchy/events', {
      params: {
        tenant_id: query.tenantId,
        // Comma-separated, because a repeated query parameter and an array are
        // two different wire shapes and only one of them survives every proxy.
        event_types: query.eventTypes?.length
          ? query.eventTypes.join(',')
          : undefined,
        limit: query.limit,
        before_id: query.beforeId,
      },
    })
    return response.data
  }
}

export const hierarchyService = new HierarchyService()
